/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { gnoment } from 'shared-ui/gnoment';
import PropTypes from 'prop-types';
import { DividerNew as Divider, Text, Box, Stack, Heading } from 'sarsaparilla';
import CostsTable from 'ui-sitepass/src/components/CostsTable.jsx';
import {
    passTypesSelectors,
    facilityByIdOrSlugSelectors,
} from 'ui-sitepass/src/reducers';
import { getSeasonDates } from 'ui-sitepass/src/utils';
import { getDuration, validToDate } from 'ui-sitepass/src/utils/duration_utils';
import { isDailyPassType } from 'ui-sitepass/src/utils/date_time_utils';
import { PRICE_MAGNITUDE } from 'ui-sitepass/src/constants';
import { getUnitCardinality } from 'ui-permit/src/utils/utilsNew';
import { updateSitePassTotalPrice } from '../../../actions/orderDetailsActions';

function SitePassSummary({ showSitePasses }) {
    const dispatch = useDispatch();
    const hasErrorInPass = useRef(false);
    const sitePassFacility = useSelector(
        facilityByIdOrSlugSelectors?.getFacilityByIdOrSlug
    );
    const reservations = useSelector(
        (state) => state?.timedEntry?.orderDetails?.sitePass?.reservations
    );
    const allSitePassTypes = useSelector(passTypesSelectors.getPassTypes);

    const sitePassTypes = useMemo(() => {
        const hasPassTypes = !!allSitePassTypes?.length;

        if (!hasPassTypes) {
            return allSitePassTypes;
        }

        const passTypes = allSitePassTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return passTypes;
    }, [allSitePassTypes]);

    useEffect(() => {
        const totalCost = reservations?.reduce(
            (accumulator, currentValue) =>
                accumulator + (currentValue.total ?? 0) / PRICE_MAGNITUDE,
            0
        );
        dispatch(updateSitePassTotalPrice(totalCost));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservations]);

    const buildCosts = (passType, reservation) => {
        const daySegmentGroups = passType?.features?.['day-segment']?.groups ?? {};
        const daySegmentAttendees = reservation?.features?.['day-segment']?.groups ?? {};
        const groups = passType?.features?.['attendee-info'] ?? daySegmentGroups;
        const attendees = reservation?.features?.['attendee-info'] ?? daySegmentAttendees;
        const attendeeInfo = [];
        const costs = [];
        if (reservation.fees) {
            costs.push({
                title: `Service Fee`,
                quantity: 'x 1',
                value: reservation.fees,
            });
        }
        const total = { title: 'Subtotal', value: reservation.total };
        let allowedToBuy = true;

        Object.entries(groups).forEach(([key, group]) => {
            const count = attendees[key];

            if (!count) {
                return;
            }

            if (
                group.attendee_max > -1 &&
                group.price_per_attendee &&
                count > group.attendee_max
            ) {
                allowedToBuy = false;
            }

            attendeeInfo.push({
                key,
                value: `${count} ${getUnitCardinality(
                    count,
                    key.replace(/s$|ren$/, ''),
                    key
                )} `,
            });

            let cost;

            cost = Math.max(group.price_min, count * group.price_per_attendee);
            if (group.price_max > 0) {
                cost = Math.min(group.price_max, cost);
            }

            if (cost) {
                costs.push({
                    title: passType.name,
                    quantity: `x 1`,
                    description: passType.description,
                    value: cost,
                });

                total.value += cost;
            }
        });

        return { allowedToBuy, attendeeInfo, costs, total };
    };

    const renderAttendeeInfo = (attendeeInfo) =>
        attendeeInfo.map(({ key, value }) => (
            <span key={key} className="parkpass-reservation-summary-attendee-info">
                {value}
            </span>
        ));

    const renderCostTable = (
        allowedToBuy,
        costs,
        total,
        passTypeName,
        startDate,
        endDate,
        attendeeInfo,
        index
    ) => {
        if (allowedToBuy) {
            return (
                <CostsTable
                    lines={costs}
                    total={total}
                    ariaLabel={`Pass Summary: ${
                        index + 1
                    } ${passTypeName}, The start date is ${startDate} and is valid through ${endDate}`}
                    attendeeInfo={renderAttendeeInfo(attendeeInfo)}
                />
            );
        }

        return (
            <Text fontWeight="semibold">
                Your selections are outside the bounds for this pass type. Please make a
                different selection.
            </Text>
        );
    };

    const renderSitePassReservation = (reservation, index) => {
        const passType = sitePassTypes.find(({ id }) => id === reservation.pass_type_id);
        if (!passType) {
            return null;
        }

        const { allowedToBuy, attendeeInfo, costs, total } = buildCosts(
            passType,
            reservation
        );
        const isSeasonal = ['season', 'seasonal'].includes(passType.duration);
        const DATE_FORMAT = 'ddd, ll';
        const [seasonStart] = getSeasonDates(passType.periods);
        let [, seasonEnd] = getSeasonDates(passType.periods);
        const startDateMoment = gnoment(
            isSeasonal ? seasonStart : reservation?.pass_info?.features['start-date']
        );

        let startDate = startDateMoment.isValid()
            ? startDateMoment.format(DATE_FORMAT)
            : 'Select Start Date';
        let endDate = startDateMoment.isValid()
            ? validToDate(startDateMoment, passType).format(DATE_FORMAT)
            : 'Select Start Date';

        // Some durations are flexible/user-customizable, so we have to calculate them instead of using durations.
        const duration = getDuration(passType.duration);
        const canModifyEndDate =
            passType.features['end-date'] ||
            passType.features['day-segment'] ||
            passType.duration === 'segment';
        if (canModifyEndDate) {
            const end = reservation.pass_info.features['end-date'];
            duration.days = gnoment(end).add(2, 'days').diff(gnoment(), 'days');
        }
        if (isSeasonal) {
            // For seasonal pricing, we have to get the end date ourselves.
            duration.days = gnoment(seasonEnd).add(2, 'days').diff(gnoment(), 'days');

            endDate = gnoment(seasonEnd).format(DATE_FORMAT);
        }

        if (!gnoment(seasonEnd).isValid()) {
            const [year, month, day] = seasonEnd?.split('-') ?? [];
            const adjustedYear = parseInt(year, 10) + 1;
            seasonEnd = `${adjustedYear}-${month}-${day}`;
            endDate = gnoment(seasonEnd).format(DATE_FORMAT);
        }

        const isLimitedToSeason = passType.duration.indexOf('limit-to-season') !== -1;

        if (isLimitedToSeason) {
            const days = duration.days - 1;
            const endValue = gnoment(startDateMoment).add(days, 'd');
            const isAfterSeasonEnd = endValue.isAfter(seasonEnd, 'day');
            endDate = endValue.format(DATE_FORMAT);

            if (isAfterSeasonEnd) {
                endDate = gnoment(seasonEnd).format(DATE_FORMAT);
            }
        }

        const isEmptyDailyPassDate =
            isDailyPassType(passType?.duration) &&
            isEmpty(reservation?.pass_info?.features['start-date']);

        if (isEmptyDailyPassDate) {
            startDate = 'Select Start date';
            endDate = 'Select Start date';
        }

        hasErrorInPass.current = hasErrorInPass.current || !allowedToBuy;

        return (
            <Box key={`${reservation.id}${index}`} border="gray">
                <Box background="light-blue" paddingX="sm" paddingY="xs">
                    <Text fontWeight="semibold">
                        {index + 1}. {passType.name}
                    </Text>
                </Box>
                <Box paddingX="sm" paddingY="xs">
                    <Stack>
                        <div>
                            <Text size="sm">Start Date</Text>
                            <Text fontWeight="semibold">{startDate}</Text>
                        </div>
                        <div>
                            <Text size="sm">Valid Through</Text>
                            <Text fontWeight="semibold">{endDate}</Text>
                        </div>
                        {renderCostTable(
                            allowedToBuy,
                            costs,
                            total,
                            passType?.name,
                            startDate,
                            endDate,
                            attendeeInfo,
                            index
                        )}
                    </Stack>
                </Box>
            </Box>
        );
    };

    const renderSitePassReservations = () => {
        return reservations?.map((reservation, index) => {
            return renderSitePassReservation(reservation, index);
        });
    };

    if (!showSitePasses || !reservations?.length) {
        return null;
    }

    return (
        <>
            <Divider />
            <Stack>
                <Stack space="xxs">
                    <Heading headingLevel={2} appearance="h6">
                        Site Pass
                    </Heading>
                    <Text size="sm" color="gray">
                        {sitePassFacility?.name}
                    </Text>
                </Stack>
                <Stack>{renderSitePassReservations()}</Stack>
            </Stack>
        </>
    );
}

SitePassSummary.propTypes = { showSitePasses: PropTypes.bool };

export default SitePassSummary;
